import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Send,
  X,
  Loader2,
  CheckCircle,

  Calendar,
  CheckSquare,
  User,
  FileText,
  Search,
  TrendingUp
} from 'lucide-react';
import { OctoAIIcon } from './OctoAIIcon';
import { useAuth } from '../../contexts/AuthContext';
import './OctoAIChatbot.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api/v1';

interface Message {
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  tool_calls?: any[];
}

interface OctoAIChatbotProps {
  isOpen: boolean;
  onClose: () => void;
}

const EXAMPLE_PROMPTS = [
  { icon: Calendar, text: 'Zeige mir meine heutigen Termine', category: 'Kalender' },
  { icon: CheckSquare, text: 'Erstelle eine Aufgabe für morgen', category: 'Tasks' },
  { icon: User, text: 'Suche Kontakt Max Mustermann', category: 'Kontakte' },
  { icon: FileText, text: 'Erstelle eine Notiz', category: 'Notizen' },
  { icon: Search, text: 'Zeige mir offene Tasks', category: 'Tasks' },
  { icon: TrendingUp, text: 'Gib mir eine Tagesübersicht', category: 'Übersicht' }
];

export const OctoAIChatbot: React.FC<OctoAIChatbotProps> = ({
  isOpen,
  onClose
}) => {
  const { user } = useAuth();
  const userId = user?.id || '';
  const companyId = user?.company_id || '';
  const [messages, setMessages] = useState<Message[]>([
    {
      role: 'assistant',
      content: 'Hallo! Ich bin OctoAI, dein CRM-Assistent. Ich kann dir helfen bei:\n\n• Termine erstellen und verwalten\n• Aufgaben erstellen\n• Kontakte suchen und anlegen\n• Notizen erstellen\n• Tagesübersicht anzeigen\n\nWie kann ich dir helfen?',
      timestamp: new Date()
    }
  ]);
  const [input, setInput] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [showExamples, setShowExamples] = useState(true);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const sendMessage = async (messageText?: string) => {
    const textToSend = messageText || input.trim();
    if (!textToSend || isProcessing) return;

    setShowExamples(false);
    setInput('');

    // Add user message
    const userMessage: Message = {
      role: 'user',
      content: textToSend,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setIsProcessing(true);

    try {
      // Send to backend
      const response = await fetch(`${API_URL}/ai/octo-chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          messages: [...messages, userMessage].map(m => ({
            role: m.role,
            content: m.content
          })),
          userId,
          companyId
        })
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to send message');
      }

      // Add assistant response
      const assistantMessage: Message = {
        role: 'assistant',
        content: data.message,
        timestamp: new Date(),
        tool_calls: data.tool_calls
      };

      setMessages(prev => [...prev, assistantMessage]);

    } catch (error) {
      console.error('Chat error:', error);

      // Add error message
      const errorMessage: Message = {
        role: 'assistant',
        content: 'Entschuldigung, es gab einen Fehler bei der Verarbeitung deiner Anfrage. Bitte versuche es erneut.',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleExampleClick = (exampleText: string) => {
    setInput(exampleText);
    sendMessage(exampleText);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="octoai-chatbot-overlay"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          transition={{ type: 'spring', damping: 20, stiffness: 300 }}
          className="octoai-chatbot"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="chatbot-header">
            <div className="header-left">
              <div className="octoai-icon-wrapper">
                <OctoAIIcon size={28} animated />
              </div>
              <div>
                <h3>OctoAI Assistant</h3>
                <p>Dein intelligenter CRM-Helfer</p>
              </div>
            </div>
            <button onClick={onClose} className="close-btn">
              <X size={20} />
            </button>
          </div>

          {/* Messages */}
          <div className="chatbot-messages">
            {messages.map((message, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className={`message message-${message.role}`}
              >
                {message.role === 'assistant' && (
                  <div className="message-avatar">
                    <OctoAIIcon size={20} />
                  </div>
                )}

                <div className="message-content">
                  <p className="message-text">{message.content}</p>

                  {/* Show tool calls */}
                  {message.tool_calls && message.tool_calls.length > 0 && (
                    <div className="tool-calls">
                      {message.tool_calls.map((toolCall, i) => (
                        <div key={i} className="tool-call">
                          <CheckCircle size={14} />
                          <span>{toolCall.function.name.replace(/_/g, ' ')}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  <span className="message-time">
                    {message.timestamp.toLocaleTimeString('de-DE', {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>

                {message.role === 'user' && (
                  <div className="message-avatar user-avatar">
                    <User size={20} />
                  </div>
                )}
              </motion.div>
            ))}

            {isProcessing && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="message message-assistant"
              >
                <div className="message-avatar">
                  <OctoAIIcon size={20} />
                </div>
                <div className="message-content typing">
                  <Loader2 className="spin" size={16} />
                  <span>Denke nach...</span>
                </div>
              </motion.div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Example Prompts */}
          {showExamples && messages.length === 1 && (
            <div className="example-prompts">
              <p className="examples-label">Beispiele:</p>
              <div className="examples-grid">
                {EXAMPLE_PROMPTS.map((example, index) => {
                  const Icon = example.icon;
                  return (
                    <button
                      key={index}
                      onClick={() => handleExampleClick(example.text)}
                      className="example-prompt"
                    >
                      <Icon size={14} />
                      <span>{example.text}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Input */}
          <div className="chatbot-input-wrapper">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Frage mich etwas..."
              className="chatbot-input"
              disabled={isProcessing}
            />
            <button
              onClick={() => sendMessage()}
              disabled={!input.trim() || isProcessing}
              className="send-btn"
            >
              {isProcessing ? (
                <Loader2 className="spin" size={20} />
              ) : (
                <Send size={20} />
              )}
            </button>
          </div>

          {/* Footer */}
          <div className="chatbot-footer">
            <span>Powered by GPT-4o-mini</span>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
